/**
 * World: the mutable document. Elements live in cells (keyed by cell id)
 * and layers. The world tracks which (cell, layer) tiles are dirty so the
 * renderer can refresh raster caches.
 */

import { CellTree } from "./cell";
import {
  Bookmark,
  DEFAULT_LAYER,
  Element,
  FillElement,
  ImageElement,
  Layer,
  MAX_INLINE_IMAGE_BYTES,
  ShapeElement,
  Stroke,
  TextElement,
  WorldData,
} from "./model";

export interface WorldEvents {
  changed: { cellId: string; layerId: string };
  layersChanged: void;
  bookmarksChanged: void;
}

type Listener<K extends keyof WorldEvents> = (e: WorldEvents[K]) => void;

export interface ShapeSpec {
  shape: "rect" | "ellipse";
  x: number;
  y: number;
  w: number;
  h: number;
  style: {
    color: string;
    size: number;
    opacity: number;
    filled: boolean;
  };
}

export class World {
  readonly tree = new CellTree();
  layers: Layer[] = [{ ...DEFAULT_LAYER }];
  /** Map of stroke id -> cell/layer for O(1) lookup during hit tests. */
  private index = new Map<string, { cellId: string; layerId: string }>();

  /** The stroke currently being drawn (previewed live, not yet committed). */
  activeStroke: Stroke | null = null;

  setActiveStroke(s: Stroke | null): void {
    this.activeStroke = s;
  }
  bookmarks: Bookmark[] = [];
  /** cellId -> layerId -> elements (in insertion order). */
  private cells = new Map<string, Map<string, Element[]>>();
  private listeners = new Map<string, Set<(e: unknown) => void>>();

  /* ------------------------------ listeners ------------------------------ */

  on<K extends keyof WorldEvents>(k: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(k);
    if (!set) this.listeners.set(k, (set = new Set()));
    set.add(fn as (e: unknown) => void);
    return () => set!.delete(fn as (e: unknown) => void);
  }

  private emit<K extends keyof WorldEvents>(k: K, e: WorldEvents[K]): void {
    this.listeners.get(k)?.forEach((fn) => fn(e));
  }

  /* -------------------------------- layers ------------------------------- */

  getLayer(id: string): Layer | undefined {
    return this.layers.find((l) => l.id === id);
  }

  ensureLayer(id: string): Layer {
    const existing = this.getLayer(id);
    if (existing) return existing;
    const layer: Layer = {
      id,
      name: `Layer ${this.layers.length + 1}`,
      visible: true,
      blend: "normal",
      opacity: 1,
    };
    this.layers.push(layer);
    this.emit("layersChanged", undefined);
    return layer;
  }

  addLayer(): Layer {
    const id = `layer-${Math.random().toString(36).slice(2, 9)}`;
    return this.ensureLayer(id);
  }

  updateLayer(id: string, patch: Partial<Layer>): void {
    const layer = this.getLayer(id);
    if (!layer) return;
    Object.assign(layer, patch);
    this.emit("layersChanged", undefined);
  }

  removeLayer(id: string): void {
    if (this.layers.length <= 1) return;
    this.layers = this.layers.filter((l) => l.id !== id);
    for (const byLayer of this.cells.values()) byLayer.delete(id);
    this.emit("layersChanged", undefined);
  }

  /* ------------------------------ bookmarks ------------------------------ */

  addBookmark(
    cellId: string,
    x: number,
    y: number,
    scaleOrLabel?: number | string,
    label?: string,
  ): Bookmark {
    const scale = typeof scaleOrLabel === "number" ? scaleOrLabel : undefined;
    const resolvedLabel =
      typeof scaleOrLabel === "string" ? scaleOrLabel : (label ?? `Bookmark ${this.bookmarks.length + 1}`);
    const b: Bookmark = {
      id: `bm-${Math.random().toString(36).slice(2, 9)}`,
      cellId,
      x,
      y,
      ...(scale !== undefined ? { scale } : {}),
      label: resolvedLabel,
      createdAt: Date.now(),
    };
    this.bookmarks.push(b);
    this.emit("bookmarksChanged", undefined);
    return b;
  }

  removeBookmark(id: string): void {
    this.bookmarks = this.bookmarks.filter((b) => b.id !== id);
    this.emit("bookmarksChanged", undefined);
  }

  /* ------------------------------ elements ------------------------------- */

  elementsInCell(cellId: string, layerId?: string): Element[] {
    const byLayer = this.cells.get(cellId);
    if (!byLayer) return [];
    if (layerId !== undefined) return byLayer.get(layerId) ?? [];
    const all: Element[] = [];
    for (const arr of byLayer.values()) all.push(...arr);
    return all;
  }

  /** All elements across the world, for serialization. */
  allCells(): Record<string, Element[]> {
    const out: Record<string, Element[]> = {};
    for (const [cellId, byLayer] of this.cells) {
      const flat: Element[] = [];
      for (const arr of byLayer.values()) flat.push(...arr);
      if (flat.length) out[cellId] = flat;
    }
    return out;
  }

  addElement(el: Element): void {
    this.ensureLayer(el.layerId);
    this.tree.ensure(el.cellId);
    let byLayer = this.cells.get(el.cellId);
    if (!byLayer) this.cells.set(el.cellId, (byLayer = new Map()));
    let arr = byLayer.get(el.layerId);
    if (!arr) byLayer.set(el.layerId, (arr = []));
    arr.push(el);
    this.index.set(el.id, { cellId: el.cellId, layerId: el.layerId });
    this.markContent(el.cellId);
    this.emit("changed", { cellId: el.cellId, layerId: el.layerId });
  }

  locate(id: string): { cellId: string; layerId: string } | undefined {
    return this.index.get(id);
  }

  /** Propagate the content flag up to the root (renderer culling depends on it). */
  private markContent(cellId: string): void {
    let cell = this.tree.get(cellId);
    while (cell) {
      if (cell.hasContent) return; // ancestors already flagged
      cell.hasContent = true;
      if (cell.parentId === null) return;
      cell = this.tree.get(cell.parentId);
    }
  }

  addElements(els: Element[]): void {
    for (const el of els) this.addElement(el);
  }

  removeElement(cellId: string, layerId: string, id: string): Element | undefined {
    const arr = this.cells.get(cellId)?.get(layerId);
    if (!arr) return undefined;
    const idx = arr.findIndex((e) => e.id === id);
    if (idx < 0) return undefined;
    const [removed] = arr.splice(idx, 1);
    this.index.delete(id);
    this.emit("changed", { cellId, layerId });
    return removed;
  }

  getElement(cellId: string, layerId: string, id: string): Element | undefined {
    return this.cells.get(cellId)?.get(layerId)?.find((e) => e.id === id);
  }

  /** Replace an element in place (keeps position in z-order). */
  replaceElement(el: Element): void {
    const arr = this.cells.get(el.cellId)?.get(el.layerId);
    if (!arr) return;
    const idx = arr.findIndex((e) => e.id === el.id);
    if (idx < 0) return;
    arr[idx] = el;
    this.emit("changed", { cellId: el.cellId, layerId: el.layerId });
  }

  /** Count of elements, for stats/limits. */
  get elementCount(): number {
    let n = 0;
    for (const byLayer of this.cells.values())
      for (const arr of byLayer.values()) n += arr.length;
    return n;
  }

  /* ------------------------------- helpers ------------------------------- */

  newStroke(partial: Omit<Stroke, "id" | "kind">): Stroke {
    return { kind: "stroke", id: newId("st"), ...partial };
  }

  newText(partial: Omit<TextElement, "id" | "kind">): TextElement {
    return { kind: "text", id: newId("tx"), ...partial };
  }

  newImage(partial: Omit<ImageElement, "id" | "kind">): ImageElement | { error: string } {
    if (partial.src.length > MAX_INLINE_IMAGE_BYTES) {
      return { error: "Image is too large to embed (max 3 MB)" };
    }
    return { kind: "image", id: newId("im"), ...partial };
  }

  newShape(partial: Omit<ShapeElement, "id" | "kind">): ShapeElement {
    return { kind: "shape", id: newId("sh"), ...partial };
  }

  newFill(partial: Omit<FillElement, "id" | "kind">): FillElement {
    return { kind: "fill", id: newId("fi"), ...partial };
  }

  /* ----------------------------- serialization --------------------------- */

  toData(): WorldData {
    return {
      version: 1,
      layers: this.layers.map((l) => ({ ...l })),
      bookmarks: this.bookmarks.map((b) => ({ ...b })),
      cells: this.allCells(),
    };
  }

  load(data: WorldData): void {
    this.cells.clear();
    this.index.clear();
    this.layers = data.layers.length
      ? data.layers.map((l) => ({ ...l }))
      : [{ ...DEFAULT_LAYER }];
    this.bookmarks = data.bookmarks.map((b) => ({ ...b }));
    // Bookmarks may reference cells that hold no content; recreate their
    // paths so navigation never hits a missing cell.
    for (const b of this.bookmarks) {
      try {
        this.tree.ensure(b.cellId);
      } catch {
        this.bookmarks = this.bookmarks.filter((x) => x.id !== b.id);
      }
    }
    for (const [cellId, els] of Object.entries(data.cells)) {
      this.tree.ensure(cellId);
      for (const el of els) {
        let byLayer = this.cells.get(cellId);
        if (!byLayer) this.cells.set(cellId, (byLayer = new Map()));
        let arr = byLayer.get(el.layerId);
        if (!arr) byLayer.set(el.layerId, (arr = []));
        arr.push(el);
        this.index.set(el.id, { cellId: el.cellId, layerId: el.layerId });
        this.markContent(el.cellId);
      }
    }
    // Everything is dirty after a load.
    for (const [cellId, byLayer] of this.cells)
      for (const layerId of byLayer.keys()) this.emit("changed", { cellId, layerId });
    this.emit("layersChanged", undefined);
    this.emit("bookmarksChanged", undefined);
  }

  clear(): void {
    this.cells.clear();
    this.index.clear();
    this.layers = [{ ...DEFAULT_LAYER }];
    this.bookmarks = [];
    this.emit("layersChanged", undefined);
    this.emit("bookmarksChanged", undefined);
  }
}

export function newId(prefix: string): string {
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  return `${prefix}-${rand}`;
}

export type { ShapeElement };
